interface ProgressBarProps {
  currentStep: number;
  totalSteps: number;
  onStepClick?: (step: number) => void;
}

export function ProgressBar({ currentStep, totalSteps, onStepClick }: ProgressBarProps) {
  return (
    <div className="relative mb-12 w-full max-w-[1232px] mx-auto">
      {/* Progress Container - Exact Figma specifications */}
      <div 
        className="relative w-full bg-white shadow-sm"
        style={{ 
          height: '100px',
          borderRadius: '90px',
          background: 'linear-gradient(white, white) padding-box, linear-gradient(90deg, rgba(127, 45, 147, 0.3) 0%, rgba(255, 252, 244, 0.3) 41.83%, rgba(233, 91, 123, 0.3) 95.67%) border-box',
          border: '2px solid transparent'
        }}
      >
        {/* Individual line segments between steps */}
        <div className="absolute top-1/2 left-20 right-20 transform -translate-y-1/2">
          <div className="relative flex items-center justify-between w-full">
            {Array.from({ length: totalSteps - 1 }, (_, index) => {
              const lineIndex = index + 1; // Line 1-2, 2-3, 3-4
              const isPurple = lineIndex <= currentStep;
              const isFirstLine = index === 0;
              const isLastLine = index === totalSteps - 2;
              
              return (
                <div 
                  key={lineIndex}
                  className="flex-1 h-px transition-all duration-300"
                  style={{
                    backgroundColor: isPurple ? 'rgba(127, 45, 147, 1)' : '#E5E7EB',
                    marginLeft: isFirstLine ? '16px' : '8px',
                    marginRight: isLastLine ? '16px' : '8px'
                  }}
                />
              );
            })}
          </div>
        </div>
        
        {/* Step circles */}
        <div className="absolute top-1/2 left-20 right-20 transform -translate-y-1/2">
          <div className="relative flex items-center justify-between w-full">
            {Array.from({ length: totalSteps }, (_, index) => {
              const stepNumber = index + 1;
              const isActive = stepNumber === currentStep;
              const isCompleted = stepNumber < currentStep;
              const isNextStep = stepNumber === currentStep + 1;
              const isClickable = onStepClick && stepNumber < currentStep;
              
              return (
                <div key={stepNumber} className="relative">
                  {/* Step Label - positioned above current step circle */}
                  {isActive && (
                    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-gray-500 text-sm whitespace-nowrap">
                      Step
                    </div>
                  )}
                  
                  <div
                    className={`
                      w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 relative z-10
                      ${isCompleted || isActive
                        ? 'text-white'
                        : isNextStep
                          ? 'bg-white border-2'
                          : 'bg-gray-100 border-2 border-gray-300'
                      }
                      ${isClickable ? 'cursor-pointer hover:scale-105' : ''}
                    `}
                    style={{
                      backgroundColor: isCompleted || isActive ? 'rgba(127, 45, 147, 1)' : undefined,
                      borderColor: isNextStep ? 'rgba(127, 45, 147, 1)' : undefined
                    }}
                    onClick={isClickable ? () => onStepClick(stepNumber) : undefined}
                  >
                    {isCompleted || isActive ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : isNextStep ? (
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'rgba(127, 45, 147, 1)' }}></div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Step Counter Badge - positioned to straddle the border */}
      <div 
        className="absolute bottom-0 left-16 transform translate-y-1/2 text-green-medium text-sm font-medium"
        style={{
          backgroundColor: 'white',
          padding: '8px 16px',
          borderRadius: '90px'
        }}
      >
        {currentStep} of {totalSteps}
      </div>
    </div>
  );
}